import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { HelpCircle } from 'lucide-react-native';
import { Avatar, SignInChip } from '@components/common';
import { useTheme, SPACING, BORDER_RADIUS } from '../../theme';
import { Structure, User } from '../../types';
import { useUIStore, useMapPathsOverlayActive } from '../../store/uiStore';
import { useNotificationsStore } from '../../store/notificationsStore';
import { MAP_CONTROLS_CLEARANCE } from './MapControls';

type OpenSheet = ReturnType<typeof useUIStore.getState>['openSheet'];

const CHIP_SIZE = 36;

/**
 * Top edge of the phone corner column, measured from the bottom of the
 * screen — for anything that has to sit clear of it. Assumes the authed
 * layout (a single avatar chip), which is the only case that matters: the
 * signed-out column is taller, but nothing anchors above it.
 */
export const MAP_CORNER_CHIP_TOP = MAP_CONTROLS_CLEARANCE + CHIP_SIZE;

interface MapSignInOverlayProps {
  user: User | null;
  isDesktopShell: boolean;
  tabBarVisible: boolean;
  selectedStructure: Structure | null;
  isMobile: boolean;
  insets: { top: number; bottom: number; left: number; right: number };
  hasSeenTour: boolean;
  hasDismissedSaveNudge: boolean;
  isUserMenuOpen: boolean;
  openSheet: OpenSheet;
}

export function MapSignInOverlay({
  user,
  isDesktopShell,
  tabBarVisible,
  selectedStructure,
  isMobile,
  insets,
  hasSeenTour,
  hasDismissedSaveNudge,
  isUserMenuOpen,
  openSheet,
}: MapSignInOverlayProps) {
  const { colors } = useTheme();
  // While MapPathsOverlay is up (first-visit or a Guidance-tab reopen) this
  // corner stack (Sign In chip, (?) tour button, avatar) stays out of its
  // way — on phones the two otherwise overlap. Shared selector so this and
  // CreatePostCoachmark suppress off the same boolean MapPathsOverlay uses.
  const pathsOverlayActive = useMapPathsOverlayActive();

  // Unread alerts light the avatar dot: the Alerts tab left the nav
  // (Aug 2026) and the user menu is now the only path to /alerts.
  const unreadCount = useNotificationsStore((s) => s.unreadCount);

  const showAvatarDot =
    user?.isAnonymous === true && (user?.postsCount ?? 0) > 0 && !hasDismissedSaveNudge;

  if (isDesktopShell || !tabBarVisible || selectedStructure || pathsOverlayActive) {
    return null;
  }

  return (
    <View
      style={[
        styles.topRightCorner,
        isMobile
          ? { ...styles.topRightCornerMobile, bottom: MAP_CONTROLS_CLEARANCE + insets.bottom }
          : { top: insets.top + SPACING.sm },
      ]}
      pointerEvents="box-none">
      {user ? (
        // Show the avatar for every authenticated user, including
        // anonymous Fire Scouts. Tapping opens the user menu (which
        // includes Tour REDI, theme, and — for anon — "Save your
        // account"). The pulsing dot signals either an un-opened tour
        // or a pending save-account nudge after a first anon post.
        <TouchableOpacity
          // On phones this trigger lives in the bottom-right corner stack,
          // so the dropdown must anchor there instead of under the header.
          onPress={() =>
            openSheet('userMenu', { userMenuAnchor: isMobile ? 'mapCorner' : 'header' })
          }
          accessibilityRole="button"
          accessibilityLabel="Open user menu"
          testID="map-avatar-button"
          hitSlop={8}
          style={styles.avatarWrap}>
          <Avatar
            size={CHIP_SIZE}
            name={user.isAnonymous ? '' : `${user.firstName} ${user.lastName}`}
            imageUrl={user.isAnonymous ? null : user.avatarUrl}
            anonymous={user.isAnonymous === true}
            showIndicator={(!hasSeenTour || showAvatarDot || unreadCount > 0) && !isUserMenuOpen}
          />
        </TouchableOpacity>
      ) : (
        <>
          {/* Pre-signup education: signed-out visitors have no avatar
              (and so no user menu), so Tour REDI gets its own trigger.
              The dot mirrors the avatar's unseen-tour indicator. */}
          <TouchableOpacity
            onPress={() => openSheet('howItWorks')}
            accessibilityRole="button"
            accessibilityLabel="Learn how REDI works"
            testID="map-help-button"
            hitSlop={8}
            style={[
              styles.helpButton,
              { backgroundColor: colors.surface, borderColor: colors.border },
            ]}>
            <HelpCircle size={18} color={colors.primary} />
            {!hasSeenTour && <View style={[styles.helpDot, { backgroundColor: colors.primary }]} />}
          </TouchableOpacity>
          <SignInChip variant="pill" testID="map-sign-in-button" />
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  topRightCorner: {
    position: 'absolute',
    right: SPACING.md,
    zIndex: 110,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  // Mobile override: stack vertically in the bottom-right, above MapControls
  // (the vertical offset comes from MAP_CONTROLS_CLEARANCE so it tracks the
  // control stack's height). The 15px right offset
  // aligns the icon centers (36px wide) with the map controls' visual center
  // (40px button + 1px border on each side, anchored at right:12 → center at
  // right:33). Without this nudge the icons sit 3px further right than the
  // controls beneath them and read as misaligned.
  topRightCornerMobile: {
    right: 15,
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  avatarWrap: {
    width: CHIP_SIZE,
    height: CHIP_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  helpButton: {
    width: CHIP_SIZE,
    height: CHIP_SIZE,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  helpDot: {
    position: 'absolute',
    top: -1,
    right: -1,
    width: 9,
    height: 9,
    borderRadius: 5,
  },
});
